import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const LastUpdated = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
    color: var(--text-color);
  }

  p {
    line-height: 1.8;
    color: var(--gray-dark);
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    line-height: 1.8;
    color: var(--gray-dark);
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: var(--text-color);
  }

  a {
    color: var(--primary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;

  th, td {
    padding: 0.75rem;
    text-align: left;
    border: 1px solid var(--gray-light);
  }

  th {
    background-color: var(--gray-light);
    font-weight: 600;
    color: var(--text-color);
  }

  td {
    color: var(--gray-dark);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;

    th, td {
      padding: 0.5rem;
    }
  }
`;

const HighlightBox = styled.div`
  background-color: #f0f8ff;
  border-left: 4px solid var(--primary-color);
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: 4px;

  h3 {
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    margin-bottom: 0.25rem;
  }
`;

const ContactBox = styled.div`
  background-color: var(--gray-light);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const RefundPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('refundPolicy.title')}</Title>

      <Section>
        <p>
          {t('refundPolicy.intro')}<Link to="/terms">{t('refundPolicy.introLinkText')}</Link>.
        </p>
      </Section>

      <HighlightBox>
        <h3>{t('refundPolicy.highlightBox.heading')}</h3>
        <p>{t('refundPolicy.highlightBox.text')}</p>
      </HighlightBox>

      <Section>
        <h2>{t('refundPolicy.section1.heading')}</h2>

        <h3>{t('refundPolicy.section1.sub1Heading')}</h3>
        <p>{t('refundPolicy.section1.sub1Intro')}</p>
        <ul>
          <li><strong>{t('refundPolicy.section1.orderCancellationLabel')}</strong> {t('refundPolicy.section1.orderCancellationText')}</li>
          <li><strong>{t('refundPolicy.section1.weCancelLabel')}</strong> {t('refundPolicy.section1.weCancelText')}</li>
          <li><strong>{t('refundPolicy.section1.qualityIssuesLabel')}</strong> {t('refundPolicy.section1.qualityIssuesText')}</li>
          <li><strong>{t('refundPolicy.section1.nonDeliveryLabel')}</strong> {t('refundPolicy.section1.nonDeliveryText')}</li>
          <li><strong>{t('refundPolicy.section1.incorrectOrderLabel')}</strong> {t('refundPolicy.section1.incorrectOrderText')}</li>
        </ul>

        <h3>{t('refundPolicy.section1.sub2Heading')}</h3>
        <p>{t('refundPolicy.section1.sub2Intro')}</p>
        <ul>
          <li>{t('refundPolicy.section1.sub2Item1')}</li>
          <li>{t('refundPolicy.section1.sub2Item2')}</li>
          <li>{t('refundPolicy.section1.sub2Item3')}</li>
        </ul>

        <h3>{t('refundPolicy.section1.sub3Heading')}</h3>
        <p>{t('refundPolicy.section1.sub3Intro')}</p>
        <ul>
          <li>{t('refundPolicy.section1.sub3Item1')}</li>
          <li>{t('refundPolicy.section1.sub3Item2')}</li>
          <li>{t('refundPolicy.section1.sub3Item3')}</li>
          <li>{t('refundPolicy.section1.sub3Item4')}</li>
          <li>{t('refundPolicy.section1.sub3Item5')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section2.heading')}</h2>

        <h3>{t('refundPolicy.section2.sub1Heading')}</h3>
        <p>{t('refundPolicy.section2.sub1Intro')}</p>
        <ul>
          <li><strong>{t('refundPolicy.section2.onlineOrdersLabel')}</strong> {t('refundPolicy.section2.onlineOrdersText')}</li>
          <li><strong>{t('refundPolicy.section2.inStoreLabel')}</strong> {t('refundPolicy.section2.inStoreText')}</li>
          <li><strong>{t('refundPolicy.section2.byPhoneLabel')}</strong> {t('refundPolicy.section2.byPhoneText')}</li>
        </ul>

        <h3>{t('refundPolicy.section2.sub2Heading')}</h3>
        <p>{t('refundPolicy.section2.sub2Intro')}</p>
        <ul>
          <li>{t('refundPolicy.section2.sub2Item1')}</li>
          <li>{t('refundPolicy.section2.sub2Item2')}</li>
          <li>{t('refundPolicy.section2.sub2Item3')}</li>
          <li>{t('refundPolicy.section2.sub2Item4')}</li>
          <li>{t('refundPolicy.section2.sub2Item5')}</li>
        </ul>

        <h3>{t('refundPolicy.section2.sub3Heading')}</h3>
        <Table>
          <thead>
            <tr>
              <th>{t('refundPolicy.section2.typeHeader')}</th>
              <th>{t('refundPolicy.section2.processingTimeHeader')}</th>
              <th>{t('refundPolicy.section2.returnToAccountHeader')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('refundPolicy.section2.row1Type')}</td>
              <td>{t('refundPolicy.section2.row1ProcessingTime')}</td>
              <td>{t('refundPolicy.section2.row1ReturnToAccount')}</td>
            </tr>
            <tr>
              <td>{t('refundPolicy.section2.row2Type')}</td>
              <td>{t('refundPolicy.section2.row2ProcessingTime')}</td>
              <td>{t('refundPolicy.section2.row2ReturnToAccount')}</td>
            </tr>
            <tr>
              <td>{t('refundPolicy.section2.row3Type')}</td>
              <td>{t('refundPolicy.section2.row3ProcessingTime')}</td>
              <td>{t('refundPolicy.section2.row3ReturnToAccount')}</td>
            </tr>
            <tr>
              <td>{t('refundPolicy.section2.row4Type')}</td>
              <td>{t('refundPolicy.section2.row4ProcessingTime')}</td>
              <td>{t('refundPolicy.section2.row4ReturnToAccount')}</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section3.heading')}</h2>

        <h3>{t('refundPolicy.section3.sub1Heading')}</h3>
        <p>{t('refundPolicy.section3.sub1Intro')}</p>
        <ul>
          <li>{t('refundPolicy.section3.sub1Item1')}</li>
          <li>{t('refundPolicy.section3.sub1Item2')}</li>
          <li>{t('refundPolicy.section3.sub1Item3')}</li>
        </ul>

        <h3>{t('refundPolicy.section3.sub2Heading')}</h3>
        <p>{t('refundPolicy.section3.sub2Text')}</p>

        <h3>{t('refundPolicy.section3.sub3Heading')}</h3>
        <p>{t('refundPolicy.section3.sub3Intro')}</p>
        <ul>
          <li>{t('refundPolicy.section3.sub3Item1')}</li>
          <li>{t('refundPolicy.section3.sub3Item2')}</li>
          <li>{t('refundPolicy.section3.sub3Item3')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section4.heading')}</h2>
        <p>{t('refundPolicy.section4.intro')}</p>
        <ul>
          <li><strong>{t('refundPolicy.section4.replacementLabel')}</strong> {t('refundPolicy.section4.replacementText')}</li>
          <li><strong>{t('refundPolicy.section4.storeCreditLabel')}</strong> {t('refundPolicy.section4.storeCreditText')}</li>
          <li><strong>{t('refundPolicy.section4.discountLabel')}</strong> {t('refundPolicy.section4.discountText')}</li>
          <li><strong>{t('refundPolicy.section4.complimentaryItemsLabel')}</strong> {t('refundPolicy.section4.complimentaryItemsText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section5.heading')}</h2>
        <p>{t('refundPolicy.section5.intro')}</p>
        <ul>
          <li><strong>{t('refundPolicy.section5.creditDebitLabel')}</strong> {t('refundPolicy.section5.creditDebitText')}</li>
          <li><strong>{t('refundPolicy.section5.digitalWalletsLabel')}</strong> {t('refundPolicy.section5.digitalWalletsText')}</li>
          <li><strong>{t('refundPolicy.section5.cashPaymentsLabel')}</strong> {t('refundPolicy.section5.cashPaymentsText')}</li>
          <li><strong>{t('refundPolicy.section5.giftCardsLabel')}</strong> {t('refundPolicy.section5.giftCardsText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section6.heading')}</h2>
        <p>{t('refundPolicy.section6.intro')}</p>
        <ul>
          <li>{t('refundPolicy.section6.item1')}</li>
          <li>{t('refundPolicy.section6.item2')}</li>
          <li>{t('refundPolicy.section6.item3')}</li>
          <li>{t('refundPolicy.section6.item4')}</li>
        </ul>

        <p>{t('refundPolicy.section6.outro')}</p>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section7.heading')}</h2>
        <p>{t('refundPolicy.section7.text')}</p>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section8.heading')}</h2>
        <p>{t('refundPolicy.section8.text')}</p>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section9.heading')}</h2>
        <p>{t('refundPolicy.section9.text')}</p>
      </Section>

      <Section>
        <h2>{t('refundPolicy.section10.heading')}</h2>
        <p>{t('refundPolicy.section10.text')}</p>
      </Section>

      <ContactBox>
        <h3>{t('refundPolicy.contactBox.heading')}</h3>
        <p><strong>{t('refundPolicy.contactBox.companyName')}</strong></p>
        <p>{t('refundPolicy.contactBox.address')}</p>
        <p>{t('refundPolicy.contactBox.email')}</p>
        <p>{t('refundPolicy.contactBox.phone')}</p>
        <p>{t('refundPolicy.contactBox.hoursWeekday')}</p>
        <p>{t('refundPolicy.contactBox.hoursSunday')}</p>
      </ContactBox>
    </Container>
  );
};

export default RefundPolicy;
